import React from "react";
import Highcharts from "highcharts";
import highchartsDumbbell from "highcharts/modules/dumbbell";
import { Box } from "@material-ui/core";

import useColourPalette from "components/visuals/useColourPalette";
import Dumbbell from "components/visuals/Dumbbell";
import stringUtils from "utils/stringUtils";
import numberFormatter from "utils/numberFormatter";
highchartsDumbbell(Highcharts);

export interface DumbbellAlignmentValues {
    lowCategory: string,
    highCategory: string,
    label: string,
    selectedValue: number | null,
    targetValue: number,
    nullValueMessage?: string
}
interface DumbbellAlignmentProps {
    isLoading?: boolean,
    hasErrors?: boolean,
    chartValues: DumbbellAlignmentValues[],
    selectedSeriesName?: string,
    targetSeriesName?: string,
    targetIsSet: boolean,
    chartHeight?: number,
    dataCy?: string
}

const defaultProps: DumbbellAlignmentProps = {
    isLoading: false,
    hasErrors: false,
    chartValues: [],
    selectedSeriesName: "Selected location",
    targetSeriesName: "Target",
    targetIsSet: false,
    dataCy: ""
};

const DumbbellAlignment: React.FC<DumbbellAlignmentProps> = (props: DumbbellAlignmentProps) => {
    props = { ...defaultProps, ...props };
    let {
        isLoading, hasErrors, chartValues,
        selectedSeriesName, targetSeriesName,
        targetIsSet, chartHeight, dataCy
    } = props;
    const colourPalette = useColourPalette();
    const selectedColour = colourPalette.comparators[0];
    const targetColour = '#FFFFFF';

    const seriesNames = {
        selected: selectedSeriesName,
        target: targetSeriesName
    };

    const lowCategories = chartValues.map(value => value.lowCategory);
    const highCategories = chartValues.map(value => value.highCategory);

    const createDumbbellPointOptions = (
        x: number,
        tooltip: string,
        selectedValue: number | null,
        targetValue: number
    ): Highcharts.PointOptionsObject => {
        const low = (selectedValue === null) ? undefined : Math.min(selectedValue, targetValue);
        const high = (selectedValue === null) ? targetValue : Math.max(selectedValue, targetValue);
        return {
            x,
            low,
            high,
            lowColor: (low === selectedValue) ? colourPalette.comparators[0] : targetColour,
            color: (high === targetValue) ? targetColour : colourPalette.comparators[0],
            custom: {
                tooltip,
                lowSeries: (low === selectedValue) ? seriesNames.selected : seriesNames.target,
                highSeries: (high === targetValue) ? seriesNames.target : seriesNames.selected
            }
        };
    };

    const dumbbellData = targetIsSet ? chartValues.map((metric, index) =>
        createDumbbellPointOptions(index, metric.label, metric.selectedValue, metric.targetValue)
    ) : [];

    const selectedLocationOnlyScatterData = targetIsSet ? [] : chartValues.map(metric => ({
        y: metric.selectedValue,
        custom: {
            tooltip: metric.label
        }
    }));

    const targetOnlyScatterData = targetIsSet ? chartValues.map((metric, index) =>
        metric.selectedValue === null ? {
            x: index,
            y: metric.targetValue,
            custom: {
                tooltip: metric.label
            }
        } : null
    ).filter(Boolean) : [];

    chartHeight = chartHeight ?? 110 + (chartValues.length - 1) * 70;

    const options: Highcharts.Options = {
        chart: {
            height: chartHeight
        },
        xAxis: [{
            categories: lowCategories
        }, {
            categories: highCategories
        }],
        yAxis: {
            labels: {
                enabled: false
            },
            max: 100,
            min: 0
        },
        tooltip: {
            useHTML: true,
            headerFormat: `<table>`,
            footerFormat: `</table>`,
            pointFormatter: function () {
                const customOptions = this?.options?.custom;
                const scoreSuffix = `percentile`;
                const header = customOptions?.tooltip + " score";
                if (this.series.type === "dumbbell") {
                    return stringUtils.tooltipHTML([customOptions?.lowSeries, customOptions?.highSeries], {
                        values: [`${numberFormatter.toOrdinalNumber(this.options?.low)} ${scoreSuffix}`,
                        `${numberFormatter.toOrdinalNumber(this.options?.high)} ${scoreSuffix}`],
                        header,
                        valueFormattingArr: [`color:${this.options?.lowColor}`, `color:${this.options?.color}`]
                    });
                } else {
                    const categories = [];
                    const values = [];
                    const valueFormattingArr = [];
                    if (this.series.name === seriesNames.target) {
                        categories.push(seriesNames.selected);
                        const unavailableMetricMessage = chartValues.find(item => item.label === customOptions?.tooltip)?.nullValueMessage ?? "Unavailable";
                        values.push(`&nbsp;&nbsp;${unavailableMetricMessage}`);
                        valueFormattingArr.push(`color:${selectedColour}`);
                    }
                    categories.push(this.series.name);
                    values.push(`${numberFormatter.toOrdinalNumber(this.options?.y)} ${scoreSuffix}`);
                    valueFormattingArr.push(`color:${this.series.color}`);
                    return stringUtils.tooltipHTML(categories, {
                        values,
                        header,
                        valueFormattingArr
                    });
                }
            }
        },
        series: [{
            type: "dumbbell",
            showInLegend: false,
            connectNulls: false,
            data: dumbbellData
        }, {
            name: seriesNames.selected,
            type: "scatter",
            data: selectedLocationOnlyScatterData,
            showInLegend: true,
            color: selectedColour
        }, {
            name: seriesNames.target,
            type: "scatter",
            data: targetOnlyScatterData,
            showInLegend: targetIsSet,
            color: targetColour
        }],
    };

    return (
        <Box data-cy={dataCy}>
            <Dumbbell loading={!!isLoading} error={!!hasErrors} options={options} />
        </Box>
    );
};

export default DumbbellAlignment;
